import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/store/root-state';

import {
  fetchUserProperties,
  fetchPropertyById,
  createProperty,
  updateProperty,
  fetchOrgProperties,
  initiatePropertyHandover,
  cancelPropertyHandover,
  fetchActivePropertyHandover,
  fetchPropertyEvents,
  findPropertyByCode,
} from './propertiesThunks';
import type { CreateItemResponse } from '@/services/api/types';
import type { IProperty } from '@/domain/entities/property';
import type { PropertiesState, PropertyHandoverState } from './propertyTypes';
import { clearSettledHandovers } from './helpers';

const initialState: PropertiesState = {
  items: [],
  isListing: false,
  errorListing: null,
  isLoadingOne: false,
  isSaving: false,

  hasFetched: false,
  lastFetchedAt: null,

  // ✅ per-property dirty flags
  dirtyById: {},
  saveErrorById: {},
  savingById: {},

  handoverById: {},
  handoverInitiatingById: {},
  handoverErrorById: {},

  //Events
  eventsByPropertyId: {},
  eventsLoadingByPropertyId: {},
  eventsErrorByPropertyId: {},
  eventsHasFetchedByPropertyId: {},
  eventsLastFetchedAtByPropertyId: {},
  eventsNextTokenByPropertyId: {},
  eventsTotalByPropertyId: {},
};

const upsertById = (items: IProperty[], rec: IProperty): IProperty[] => {
  const idx = items.findIndex((x) => x.id === rec.id);
  if (idx === -1) return [rec, ...items];
  const copy = items.slice();
  copy[idx] = rec;
  return copy;
};

export const propertiesSlice = createSlice({
  name: 'properties',
  initialState,
  reducers: {
    patchSelectedProperty: (state, action: PayloadAction<{ id: string; patch: Partial<IProperty> }>) => {
      const { id, patch } = action.payload;
      if (!id) return;

      const idx = state.items.findIndex((x) => x.id === id);
      if (idx === -1) return;

      const next: IProperty = {
        ...state.items[idx],
        ...patch,
        updatedAt: Math.floor(Date.now() / 1000),
      };

      state.items[idx] = next;

      // ✅ dirty per property
      state.dirtyById[id] = true;
    },

    // ✅ explicit dirty controls (like locations)
    markPropertyDirty: (state, action: PayloadAction<{ id: string }>) => {
      state.dirtyById[action.payload.id] = true;
    },

    markPropertyClean: (state, action: PayloadAction<{ id: string }>) => {
      state.dirtyById[action.payload.id] = false;
    },

    // optional: keep if you still use it elsewhere
    setProperties: (state, action: PayloadAction<IProperty[]>) => {
      state.items = action.payload;
      state.errorListing = null;
      // IMPORTANT: do NOT clear dirtyById here
    },
  },

  extraReducers: (builder) => {
    builder
      // list
      .addCase(fetchUserProperties.pending, (state) => {
        state.isListing = true;
        state.errorListing = null;
      })
      .addCase(fetchUserProperties.fulfilled, (state, action) => {
        state.isListing = false;
        state.items = action.payload;
        state.hasFetched = true;
        state.lastFetchedAt = Date.now();
        clearSettledHandovers(state, action.payload);
      })
      .addCase(fetchUserProperties.rejected, (state, action) => {
        state.isListing = false;
        state.errorListing = action.payload?.message ?? 'Failed to fetch properties';
        state.hasFetched = true;
      })

      // get by id
      .addCase(fetchPropertyById.pending, (state) => {
        state.isLoadingOne = true;
      })
      .addCase(fetchPropertyById.fulfilled, (state, action) => {
        state.isLoadingOne = false;

        const rec = action.payload;
        state.items = upsertById(state.items, rec);

        // ✅ clean after load
        if (rec?.id) state.dirtyById[rec.id] = false;
        clearSettledHandovers(state, [rec]);
      })
      .addCase(fetchPropertyById.rejected, (state, action) => {
        state.isLoadingOne = false;
        state.errorListing = action.payload?.message ?? 'Failed to load property';
      })

      // recovery lookup
      .addCase(findPropertyByCode.fulfilled, (state, action) => {
        state.items = upsertById(state.items, action.payload);
        clearSettledHandovers(state, [action.payload]);
      })

      // create
      .addCase(createProperty.pending, (state, action) => {
        const id = action.meta.arg.property.id;
        state.savingById[id] = true;
      })
      .addCase(createProperty.fulfilled, (state, action: PayloadAction<CreateItemResponse<IProperty>>) => {
        const rec = (action.payload as unknown as { data?: IProperty }).data;
        if (!rec) return;

        state.savingById[rec.id] = false;
        state.dirtyById[rec.id] = false;
        state.saveErrorById[rec.id] = null;
        state.items = upsertById(state.items, rec);
      })
      .addCase(createProperty.rejected, (state, action) => {
        const id = action.meta.arg.property.id;
        state.savingById[id] = false;
        state.saveErrorById[id] = action.payload?.message ?? 'Failed to create property';
      })

      // update
      .addCase(updateProperty.pending, (state, action) => {
        const id = action.meta.arg.id;
        state.savingById[id] = true;
        state.saveErrorById[id] = null;
      })
      .addCase(updateProperty.fulfilled, (state, action: PayloadAction<CreateItemResponse<IProperty>>) => {
        const rec = (action.payload as unknown as { data?: IProperty }).data;
        if (!rec) return;

        state.savingById[rec.id] = false;
        state.dirtyById[rec.id] = false;
        state.saveErrorById[rec.id] = null;

        state.items = upsertById(state.items, rec);
      })
      .addCase(updateProperty.rejected, (state, action) => {
        const id = action.meta.arg.id;
        state.savingById[id] = false;
        state.saveErrorById[id] = action.payload?.message ?? 'Failed to update property';
      })

      // org list
      .addCase(fetchOrgProperties.pending, (state) => {
        state.isListing = true;
        state.errorListing = null;
      })
      .addCase(fetchOrgProperties.fulfilled, (state, action: PayloadAction<IProperty[]>) => {
        state.isListing = false;
        state.items = action.payload;
        state.hasFetched = true;
        state.lastFetchedAt = Date.now();
        clearSettledHandovers(state, action.payload);
      })
      .addCase(fetchOrgProperties.rejected, (state, action) => {
        state.isListing = false;
        state.errorListing = action.payload?.message ?? 'Failed to fetch org properties';
        state.hasFetched = true;
      })
      // handover initiate
      .addCase(initiatePropertyHandover.pending, (state, action) => {
        const propertyId = action.meta.arg.propertyID;
        state.handoverInitiatingById[propertyId] = true;
        state.handoverErrorById[propertyId] = null;
      })
      .addCase(initiatePropertyHandover.fulfilled, (state, action) => {
        const res = action.payload;
        const propertyId = res.propertyId;

        state.handoverInitiatingById[propertyId] = false;
        state.handoverErrorById[propertyId] = null;
        // The initiate response is the source of the code and claim links; it is never copied
        // into Property.handOverMeta.
        const { event, ...details } = res;
        state.handoverById[propertyId] = details;

        const idx = state.items.findIndex((x) => x.id === propertyId);
        if (idx !== -1) {
          state.items[idx] = {
            ...state.items[idx],
            status: res.status,
            updatedAt: event.eventTime ?? res.handover.initiatedAt,
          };
        }
      })
      .addCase(initiatePropertyHandover.rejected, (state, action) => {
        const propertyId = action.meta.arg.propertyID;
        state.handoverInitiatingById[propertyId] = false;
        state.handoverErrorById[propertyId] = action.payload?.message ?? 'Failed to initiate handover';
      })
      // active handover reload (after a refresh)
      .addCase(fetchActivePropertyHandover.fulfilled, (state, action) => {
        const propertyId = action.meta.arg.propertyID;
        state.handoverErrorById[propertyId] = null;
        if (action.payload) {
          state.handoverById[propertyId] = action.payload;
        } else {
          delete state.handoverById[propertyId];
        }
      })
      .addCase(fetchActivePropertyHandover.rejected, (state, action) => {
        const propertyId = action.meta.arg.propertyID;
        state.handoverErrorById[propertyId] = action.payload?.message ?? 'Failed to load the active handover';
      })
      // handover cancel
      .addCase(cancelPropertyHandover.pending, (state, action) => {
        const propertyId = action.meta.arg.propertyID;
        state.handoverInitiatingById[propertyId] = true; // reuse flag
        state.handoverErrorById[propertyId] = null;
      })
      .addCase(cancelPropertyHandover.fulfilled, (state, action) => {
        const res = action.payload;
        const propertyId = res.propertyId;

        state.handoverInitiatingById[propertyId] = false;
        state.handoverErrorById[propertyId] = null;

        // ✅ remove handover details from store
        delete state.handoverById[propertyId];

        // ✅ patch property: status + clear meta
        const idx = state.items.findIndex((x) => x.id === propertyId);
        if (idx !== -1) {
          state.items[idx] = {
            ...state.items[idx],
            status: res.status as IProperty['status'],
            handOverMeta: undefined, // or {} if you prefer
            updatedAt: res.cancelledAt,
          };
        }
      })
      .addCase(cancelPropertyHandover.rejected, (state, action) => {
        const propertyId = action.meta.arg.propertyID;
        state.handoverInitiatingById[propertyId] = false;
        state.handoverErrorById[propertyId] = action.payload?.message ?? 'Failed to cancel handover';
      })
      // property events
      .addCase(fetchPropertyEvents.pending, (state, action) => {
        const propertyId = action.meta.arg.propertyID;
        state.eventsLoadingByPropertyId[propertyId] = true;
        state.eventsErrorByPropertyId[propertyId] = null;
      })
      .addCase(fetchPropertyEvents.fulfilled, (state, action) => {
        const { propertyID, nextToken } = action.meta.arg;
        const isLoadMore = !!nextToken;
        const page = action.payload;

        state.eventsLoadingByPropertyId[propertyID] = false;
        state.eventsErrorByPropertyId[propertyID] = null;

        const prev = state.eventsByPropertyId[propertyID] ?? [];
        state.eventsByPropertyId[propertyID] = isLoadMore ? [...prev, ...page.items] : page.items;
        state.eventsNextTokenByPropertyId[propertyID] = page.nextToken ?? null;
        state.eventsHasFetchedByPropertyId[propertyID] = true;
        state.eventsLastFetchedAtByPropertyId[propertyID] = Date.now();
        state.eventsTotalByPropertyId[propertyID] = page.total ?? page.items.length;
      })
      .addCase(fetchPropertyEvents.rejected, (state, action) => {
        const propertyId = action.meta.arg.propertyID;
        state.eventsLoadingByPropertyId[propertyId] = false;
        state.eventsErrorByPropertyId[propertyId] = action.payload?.message ?? 'Failed to fetch property events';
        state.eventsHasFetchedByPropertyId[propertyId] = true;
      });
  },
});

export const { patchSelectedProperty, markPropertyDirty, markPropertyClean, setProperties } = propertiesSlice.actions;

export default propertiesSlice.reducer;

/* ---------------- selectors ---------------- */

const selectPropertiesState = (state: RootState) => state.properties;

export const selectAllProperties = createSelector([selectPropertiesState], (s) => s.items);

export const selectPropertyById = createSelector(
  [selectAllProperties, (_: RootState, id?: string | null) => id],
  (items, id) => {
    if (!id) return null;
    return items.find((p) => p.id === id) ?? null;
  },
);

export const selectIsListingProperties = createSelector([selectPropertiesState], (s) => s.isListing);
export const selectIsLoadingProperty = createSelector([selectPropertiesState], (s) => s.isLoadingOne);
export const selectIsSavingProperty = createSelector([selectPropertiesState], (s) => s.isSaving);
export const selectPropertiesHasFetched = createSelector([selectPropertiesState], (s) => s.hasFetched);
export const selectPropertiesLastFetchedAt = createSelector([selectPropertiesState], (s) => s.lastFetchedAt);

// ✅ dirty selectors
export const selectPropertyIsDirty = createSelector(
  [selectPropertiesState, (_: RootState, id?: string | null) => id],
  (s, id) => {
    if (!id) return false;
    return s.dirtyById[id] ?? false;
  },
);

export const selectDirtyPropertyIds = createSelector([selectPropertiesState], (s) =>
  Object.keys(s.dirtyById).filter((id) => s.dirtyById[id]),
);

export const selectPropertyIsSaving = createSelector(
  [selectPropertiesState, (_: RootState, propertyId?: string | null) => propertyId],
  (state, propertyId) => {
    if (!propertyId) return false;
    return state.savingById[propertyId] ?? false;
  },
);

export const selectPropertySaveError = createSelector(
  [selectPropertiesState, (_: RootState, propertyId?: string | null) => propertyId],
  (state, propertyId) => {
    if (!propertyId) return null;
    return state.saveErrorById[propertyId] ?? null;
  },
);

//Handover
export const selectPropertyHandoverState = createSelector(
  [selectPropertiesState, (_: RootState, propertyId?: string | null) => propertyId],
  (s, propertyId): PropertyHandoverState => {
    if (!propertyId) {
      return {
        initiating: false,
        error: null,
        data: null,
      };
    }

    return {
      initiating: s.handoverInitiatingById[propertyId] ?? false,
      error: s.handoverErrorById[propertyId] ?? null,
      data: s.handoverById[propertyId] ?? null,
    };
  },
);

export const selectPropertyHandoverQrPayload = createSelector(
  [selectPropertiesState, (_: RootState, propertyId?: string | null) => propertyId],
  (s, propertyId): string | null => {
    if (!propertyId) return null;

    return s.handoverById[propertyId]?.qr.payload ?? null;
  },
);

export const selectPropertyEvents = createSelector(
  [selectPropertiesState, (_: RootState, propertyId?: string | null) => propertyId],
  (s, propertyId) => {
    if (!propertyId) return [];
    return s.eventsByPropertyId[propertyId] ?? [];
  },
);

export const selectPropertyEventsLoading = createSelector(
  [selectPropertiesState, (_: RootState, propertyId?: string | null) => propertyId],
  (s, propertyId) => {
    if (!propertyId) return false;
    return s.eventsLoadingByPropertyId[propertyId] ?? false;
  },
);

export const selectPropertyEventsError = createSelector(
  [selectPropertiesState, (_: RootState, propertyId?: string | null) => propertyId],
  (s, propertyId) => {
    if (!propertyId) return null;
    return s.eventsErrorByPropertyId[propertyId] ?? null;
  },
);

export const selectPropertyEventsNextToken = createSelector(
  [selectPropertiesState, (_: RootState, propertyId?: string | null) => propertyId],
  (s, propertyId) => {
    if (!propertyId) return null;
    return s.eventsNextTokenByPropertyId[propertyId] ?? null;
  },
);

export const selectPropertyEventsHasFetched = createSelector(
  [selectPropertiesState, (_: RootState, propertyId?: string | null) => propertyId],
  (s, propertyId) => {
    if (!propertyId) return false;
    return s.eventsHasFetchedByPropertyId[propertyId] ?? false;
  },
);

export const selectPropertyEventsCanLoadMore = createSelector(
  [selectPropertiesState, (_: RootState, propertyId?: string | null) => propertyId],
  (s, propertyId) => {
    if (!propertyId) return false;
    const next = s.eventsNextTokenByPropertyId[propertyId] ?? null;
    const loading = s.eventsLoadingByPropertyId[propertyId] ?? false;

    // ✅ enable if we have a cursor and we aren't already loading
    return !!next && !loading;
  },
);

export const selectPropertyEventsTotal = createSelector(
  [selectPropertiesState, (_: RootState, propertyId?: string | null) => propertyId],
  (s, propertyId) => {
    if (!propertyId) return 0;
    return s.eventsTotalByPropertyId[propertyId] ?? 0;
  },
);
